import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IoMdSend, IoMdInformationCircleOutline } from "react-icons/io";
import { MdOutlineAttachment } from "react-icons/md";
import { useTranslation } from "react-i18next";
import type { ChatState, AiProfile } from "./chatState";
import { initialState } from "./chatState";
import TypingIndicator from "./TypingIndicator";
import {
  getCurrentUser,
  syncUserWithServer,
  updateUser,
  getCurrentOrganization,
} from "src/features/profile/state/profileSlice";
import MuiTooltip from "@mui/material/Tooltip";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import {
  resetActiveGptConversation,
  updateChat,
  setActiveGptConversation,
  fetchGptConversations,
  setChosenLlmProvider,
  getChosenLlmProvider,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
  startPlanModeChat,
  continuePlanModeChat,
  duplicatePlanThunk,
  transitionToReady,
  refinePlanThunk,
  getScenarioHelperContextPdfs,
  getScenarioHelperContextImages,
  getPlanModeProgress,
  getGenerationStartedAt,
  addScenarioHelperContextPdf,
  addScenarioHelperContextImage,
  setScenarioHelperContextImages,
  resetScenarioHelperContextPdfs,
  resetScenarioHelperContextImages,
} from "../state/chatbotSlice";
import ChatBubble from "./ChatBubble";
import GraphBubble from "./GraphBubble";
import type {
  IGptConversation,
  IUserPreferences,
  ScenariaReferenceImageChange,
} from "src/model/model";
import type { IMedia } from "src/model/unityObject";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import PlanPreview from "./PlanPreview";
import GenerationRecap from "./GenerationRecap";
import PlanModeProgress from "./PlanModeProgress";
import SkipIntakeButton from "./SkipIntakeButton";
import { canSkipIntake } from "../utils/skipThreshold";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { shallowEqual } from "react-redux";
import { GPT_CONVERSATION_LOADER_COMPONENT_ID } from "./GptConversationLoader";
import ConfigureGenerationPanel from "./configureGeneration/ConfigureGenerationPanel";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import GeneratePdfFromScenarioHelperResponse from "./GeneratePdfFromScenarioHelperResponse";
import { generateHashFromString } from "../utils/generateHashFromString";
import { isMarkdownShapedLikeAScenario } from "../utils/isMarkdownShapedLikeAScenario";
import { renderPlanToMarkdown } from "../utils/renderPlanToMarkdown";
import LlmProviderDropdown from "./LlmProviderDropdown";
import { useNavigate } from "react-router-dom";
import {
  setCurrentProject,
  setCurrentProjectPermissions,
  getProjectPermissionsFromServer,
} from "src/features/projects/state/projectsSlice";
import {
  fetchAllCurrentElements,
  fetchSpecificProjectGroupWithProjects,
  type IProjectGroupElementsFromCustomAPI,
} from "src/features/projectGroups/state/projectGroupsSlice";
import type { IProject } from "src/model/model";
import "./Chatbot.scss";
import getUserPreferredLlmProvider from "../utils/getUserPrefferedLlmProvider";
import { useDictation } from "src/features/tts-and-stt/hooks/useDictation";
import { useConversationMode } from "src/features/tts-and-stt/hooks/useConversationMode";
import MicrophoneButton from "src/features/tts-and-stt/components/MicrophoneButton";
import VoiceModeToggle from "src/features/tts-and-stt/components/VoiceModeToggle";
import ConversationStatusStrip from "src/features/chatbot/components/ConversationStatusStrip";
import AttachContextMedia, { isMediaProcessing } from "./AttachContextMedia";
import DecorStatusRow from "./referenceImage/DecorStatusRow";
import ReferenceImageGalleryHost, {
  openReferenceImageGallery,
} from "./referenceImage/ReferenceImageGalleryHost";
import useRetryImageAnalysis from "./referenceImage/useRetryImageAnalysis";
import { Tooltip } from "@mui/material";
import ContextSelector from "src/common/components/ContextSelector/ContextSelector";
import type { ScenariaOptionalLayer } from "../utils/scenariaContextPrefs";
import {
  getExcludedScenariaLayers,
  setExcludedScenariaLayers,
  SCENARIA_MANDATORY_LAYERS,
  SCENARIA_OPTIONAL_LAYERS,
} from "../utils/scenariaContextPrefs";

interface Message {
  text: string;
  sender: "bot" | "user";
}

interface ChatbotProps {
  mode: "scenarioHelper";
  activeConversation?: IGptConversation | null;
  lockedDestinationProjectGroupId?: number;
  disableAutoResume?: boolean;
}

export const CHATBOT_COMPONENT_ID = "chatbotComponentId";

const replaceReferenceImageConfirmId = "replaceReferenceImageConfirm";
const removeReferenceImageConfirmId = "removeReferenceImageConfirm";

const Chatbot: React.FC<ChatbotProps> = ({
  mode,
  activeConversation,
  lockedDestinationProjectGroupId,
  disableAutoResume,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpeningModule, setIsOpeningModule] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);

  const [chatState, setChatState] = useState<ChatState>(initialState);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState("");
  const [inputMode, setInputMode] = useState(true);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editMetier, setEditMetier] = useState("");
  const [editTargetAudience, setEditTargetAudience] = useState("");
  const [editSector, setEditSector] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [orgAiContextOverride, setOrgAiContextOverride] = useState<{
    sector?: string;
    description?: string;
  } | null>(null);
  const [sessionConversationId, setSessionConversationId] = useState<number | null>(null);
  const [pendingRemoveReferenceImage, setPendingRemoveReferenceImage] = useState<IMedia | null>(
    null,
  );
  const [pendingReplaceReferenceImage, setPendingReplaceReferenceImage] = useState<IMedia | null>(
    null,
  );
  const [isRefPicking, setIsRefPicking] = useState(false);
  const [realigningDecorTo, setRealigningDecorTo] = useState<"image" | "ai" | null>(null);
  const isRealigningDecor = realigningDecorTo !== null;

  const bottomRef = useRef<HTMLDivElement>(null);

  const user = useAppSelector(getCurrentUser, shallowEqual);
  const organization = useAppSelector(getCurrentOrganization);
  const chosenLlmProvider = useAppSelector(getChosenLlmProvider);
  const ragAgentIds = useAppSelector(getScenarioHelperRagAgentIds, shallowEqual);
  const contextPdfs = useAppSelector(getScenarioHelperContextPdfs);
  const contextImages = useAppSelector(getScenarioHelperContextImages);
  const planProgress = useAppSelector(getPlanModeProgress);
  const generationStartedAt = useAppSelector(getGenerationStartedAt);
  const currentConversationId = activeConversation?.id ?? sessionConversationId;
  const isProgressOwnConversation =
    planProgress?.conversationId != null &&
    currentConversationId != null &&
    Number(planProgress.conversationId) === Number(currentConversationId);
  const ownProgressMessage = isProgressOwnConversation ? planProgress?.message ?? null : null;
  const phase = activeConversation?.plan_phase ?? null;
  const plan = activeConversation?.conversation_metadata?.plan;
  const isPlanMode = phase !== null && phase !== undefined;
  const isLegacyConversation = Boolean(
    mode === "scenarioHelper" && activeConversation && !activeConversation.plan_phase,
  );
  const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);
  const [uiStage, setUiStage] = useState<"thread" | "configure">("thread");
  useEffect(() => {
    setLegacyBannerDismissed(false);
    setUiStage("thread");
    setPendingReplaceReferenceImage(null);
    setPendingRemoveReferenceImage(null);
    setIsRefPicking(false);
  }, [activeConversation?.id, dispatch]);
  useEffect(() => {
    if (phase !== "ready_to_generate") {
      setUiStage("thread");
    }
  }, [phase]);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;
  const [showIntakeHistory, setShowIntakeHistory] = useState(false);
  const [intakeHistoryBoundary, setIntakeHistoryBoundary] = useState<number | null>(null);
  const lastConvIdForBoundaryRef = useRef<number | undefined>(undefined);
  const lastConvIdForOverrideRef = useRef<number | null>(null);
  const sessionStartedNewConvRef = useRef(false);

  const hasMediaProcessing = useMemo(
    () => [...contextPdfs, ...contextImages].some(isMediaProcessing),
    [contextPdfs, contextImages],
  );

  const isSubmitDisabled = useMemo(
    () => !userInput.trim() || isSubmitting || hasMediaProcessing,
    [userInput, isSubmitting, hasMediaProcessing],
  );

  const submitDisabledReason = !isSubmitDisabled
    ? ""
    : hasMediaProcessing
    ? t("pages.chat.fileProcessingReason")
    : isSubmitting
    ? t("pages.chat.submitInFlightReason")
    : t("pages.chat.emptyInputReason");

  const dictationRef = useRef<ReturnType<typeof useDictation> | null>(null);
  const submitWithTextRef = useRef<((text: string) => Promise<void>) | null>(null);

  const conversation = useConversationMode({
    onAutoListenCue: () => {
      dictationRef.current?.startListening().catch(() => undefined);
    },
    onDeactivate: () => {
      dictationRef.current?.stopListening().catch(() => undefined);
    },
  });

  const dictation = useDictation({
    silenceTimeout: conversation.voiceModeEnabled ? 1500 : 30000,
    onTranscript: (text, { viaTimeout }) => {
      if (conversation.voiceModeEnabled && viaTimeout && text.trim()) {
        submitWithTextRef.current?.(text).catch(() => undefined);
      } else {
        setUserInput(text);
      }
    },
  });
  dictationRef.current = dictation;

  useEffect(() => {
    if (!dictation.isListening) return;
    const live = [dictation.transcript, dictation.interimTranscript].filter(Boolean).join(" ");
    setUserInput(live);
  }, [dictation.isListening, dictation.transcript, dictation.interimTranscript]);

  const ttsSessionOpenRef = useRef(false);

  const getSavedAiProfile = useCallback((): AiProfile | null => {
    try {
      const prefs = user?.preferences;
      if (!prefs) return null;
      const obj: IUserPreferences =
        typeof prefs === "string" ? (JSON.parse(prefs) as IUserPreferences | null) ?? {} : prefs;
      const profile = obj.aiProfile;
      if (profile?.metier) return profile;
      return null;
    } catch {
      return null;
    }
  }, [user]);

  const orgCtx = useMemo(() => {
    const ctx = orgAiContextOverride ?? organization?.ai_context;
    if (!ctx) return null;
    return ctx.sector || ctx.description ? ctx : null;
  }, [orgAiContextOverride, organization]);

  const hasUserProfile = useMemo(() => !!getSavedAiProfile()?.metier, [getSavedAiProfile]);

  const aiContextParts = useMemo(() => {
    const parts: string[] = [];
    const profile = getSavedAiProfile();
    if (profile?.metier) parts.push(profile.metier);
    if (profile?.targetAudience) parts.push(profile.targetAudience);
    if (orgCtx?.sector) parts.push(orgCtx.sector);
    if (orgCtx?.description) parts.push(orgCtx.description);
    return parts;
  }, [getSavedAiProfile, orgCtx]);

  useEffect(() => {
    if (mode === "scenarioHelper") {
      if (activeConversation) {
        loadPreviousConversation();
      } else {
        resetChatLocally();
      }
    } else {
      resetChatLocally();
    }
  }, [mode, activeConversation?.id]);

  useEffect(() => {
    const prevId = lastConvIdForOverrideRef.current;
    const currId = activeConversation?.id ?? null;
    const isSessionOwnNewConversation =
      prevId === null &&
      currId !== null &&
      (currId === sessionConversationId || sessionStartedNewConvRef.current);
    if (isSessionOwnNewConversation) {
      sessionStartedNewConvRef.current = false;
    }
    if (currId !== null && currId !== prevId && !isSessionOwnNewConversation) {
      setOrgAiContextOverride(null);
    }
    lastConvIdForOverrideRef.current = currId;
  }, [activeConversation?.id, sessionConversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  useEffect(() => {
    if (disableAutoResume) return;
    if (
      user?.active_gpt_conversation?.id &&
      user.active_gpt_conversation.chat_origin === "scenarioHelper" &&
      !activeConversationRef.current
    ) {
      dispatch(setActiveGptConversation(user.active_gpt_conversation));
    }
  }, [user?.active_gpt_conversation?.id, dispatch, disableAutoResume]);

  useEffect(() => {
    const initial = getUserPreferredLlmProvider(user?.preferences);
    dispatch(setChosenLlmProvider(initial));
  }, [user?.preferences, dispatch]);

  const loadPreviousConversation = useCallback(() => {
    if (!activeConversation) return;
    setIsEditingProfile(false);
    const { conversation_metadata, conversation_data } = activeConversation;
    setChatState((prev) => ({ ...prev, userFormResults: conversation_metadata.userFormResults }));
    const msgs: Message[] = [];
    const { userPrompts } = conversation_metadata;
    const { output } = conversation_data;
    const total = Math.max(userPrompts.length, output.length);
    for (let i = 0; i < total; i++) {
      if (i < userPrompts.length) msgs.push({ text: userPrompts[i], sender: "user" });
      if (i < output.length) msgs.push({ text: output[i], sender: "bot" });
    }
    const isSameConv = lastConvIdForBoundaryRef.current === activeConversation.id;
    lastConvIdForBoundaryRef.current = activeConversation.id;
    setMessages(msgs);

    if (activeConversation.plan_phase && activeConversation.plan_phase !== "intake") {
      const persistedBoundary = activeConversation.conversation_metadata?.intake_boundary;
      if (typeof persistedBoundary === "number") {
        setIntakeHistoryBoundary(persistedBoundary);
      } else {
        setIntakeHistoryBoundary((prev) => (isSameConv && prev !== null ? prev : msgs.length));
      }
    } else {
      setIntakeHistoryBoundary(null);
    }
    setInputMode(true);

    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());
    const sourceMedia = activeConversation.source_media_with_context || [];
    sourceMedia.forEach((m) => {
      const isImage = (m?.type || "").toLowerCase().startsWith("image");
      if (isImage) {
        dispatch(addScenarioHelperContextImage(m));
      } else {
        dispatch(addScenarioHelperContextPdf(m));
      }
    });

    const storedRagAgentIds = activeConversation.conversation_metadata?.rag_agent_ids;
    if (Array.isArray(storedRagAgentIds) && storedRagAgentIds.length > 0) {
      dispatch(setScenarioHelperRagAgentIds(storedRagAgentIds));
    } else if (ragAgentIds.length === 0) {
      dispatch(resetScenarioHelperRagAgentIds());
    }
  }, [activeConversation, dispatch, ragAgentIds]);

  const handleOpenGeneratedModule = useCallback(async () => {
    const pgId = activeConversation?.lastGeneratedProjectGroupId;
    const projectId = activeConversation?.lastGeneratedProjectId;
    if (!pgId || isOpeningModule) return;
    setIsOpeningModule(true);
    try {
      const pgRes = await dispatch(
        fetchSpecificProjectGroupWithProjects({
          projectGroupId: pgId,
          componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
        }),
      ).unwrap();
      void dispatch(fetchAllCurrentElements({ projectGroupId: pgId }));
      const pg = pgRes?.data;
      const project = projectId
        ? pg?.project_group_elements?.find(
            (pge: IProjectGroupElementsFromCustomAPI) =>
              Number(pge.project?.id) === Number(projectId),
          )?.project
        : undefined;
      if (project && projectId) {
        dispatch(setCurrentProject(project as unknown as IProject));
        const permRes = await dispatch(
          getProjectPermissionsFromServer({
            projectId: Number(projectId),
            componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
          }),
        );
        dispatch(
          setCurrentProjectPermissions(
            getProjectPermissionsFromServer.fulfilled.match(permRes) ? permRes.payload.data : [],
          ),
        );
        navigate(`/courses/${pgId}/list/${projectId}/graph`);
      } else {
        navigate(`/courses/${pgId}/list`);
      }
    } catch {
      dispatch(
        setAlert({
          msg: t("pages.chat.openModuleError"),
          type: "warning",
        }),
      );
    } finally {
      setIsOpeningModule(false);
    }
  }, [activeConversation, dispatch, isOpeningModule, navigate, t]);

  const redirectToGeneratedCourseList = useCallback(async () => {
    const pgId = activeConversation?.lastGeneratedProjectGroupId;
    if (!pgId) return;
    try {
      await dispatch(
        fetchSpecificProjectGroupWithProjects({
          projectGroupId: pgId,
          componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
        }),
      ).unwrap();
      void dispatch(fetchAllCurrentElements({ projectGroupId: pgId }));
      navigate(`/courses/${pgId}/list`);
    } catch {
      dispatch(
        setAlert({
          msg: t("pages.chat.openModuleError"),
          type: "warning",
        }),
      );
    }
  }, [activeConversation?.lastGeneratedProjectGroupId, dispatch, navigate, t]);

  const watchedGenerationRef = useRef<{ convId: number | null; phase: string | null }>({
    convId: null,
    phase: null,
  });

  useEffect(() => {
    const prev = watchedGenerationRef.current;
    const convId = activeConversation?.id ?? null;
    watchedGenerationRef.current = { convId, phase: phase ?? null };
    if (mode !== "scenarioHelper") return;
    if (prev.convId == null || convId == null) return;
    if (Number(prev.convId) !== Number(convId)) return;
    if (prev.phase === "generating" && phase === "generated") {
      void redirectToGeneratedCourseList();
    }
  }, [mode, phase, activeConversation?.id, redirectToGeneratedCourseList]);

  const handleRestartFromPlan = useCallback(async () => {
    if (!activeConversation?.id || isRestarting) return;
    setIsRestarting(true);
    const sourceName = activeConversation.name;
    try {
      const res = await dispatch(duplicatePlanThunk({ conversationId: activeConversation.id }));
      if ("error" in res && res.error) {
        dispatch(
          setAlert({
            type: "error",
            msg: t("pages.chat.generationRecap.restartFailed"),
          }),
        );
      } else {
        dispatch(
          setAlert({
            type: "success",
            msg: t("pages.chat.generationRecap.restartSuccess", { name: sourceName }),
          }),
        );
      }
    } finally {
      setIsRestarting(false);
    }
  }, [activeConversation?.id, activeConversation?.name, isRestarting, dispatch, t]);

  const resetChatLocally = useCallback(() => {
    setChatState(initialState);
    setIsBotTyping(false);
    setUserInput("");
    setSessionConversationId(null);
    sessionStartedNewConvRef.current = false;

    dispatch(resetActiveGptConversation());
    dispatch(resetScenarioHelperRagAgentIds());
    dispatch(resetScenarioHelperContextPdfs());
    dispatch(resetScenarioHelperContextImages());

    setMessages([{ text: t("pages.chat.scenarioHelperOpeningMessage"), sender: "bot" }]);
    setInputMode(true);

    setIsEditingProfile(false);
    const savedProfile = getSavedAiProfile();
    if (savedProfile?.metier) {
      setChatState((prev) => ({
        ...prev,
        userFormResults: {
          metier: savedProfile.metier,
          targetAudience: savedProfile.targetAudience,
        },
      }));
    }
  }, [mode, dispatch, getSavedAiProfile, t]);

  const updateBotMessage = useCallback(
    (chunk: string) => {
      setMessages((prev) => {
        const msgs = [...prev];
        const i = msgs.length - 1;
        msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
        return msgs;
      });
      if (ttsSessionOpenRef.current) {
        conversation.pushSpeakingChunk(chunk);
      }
    },
    [conversation],
  );

  const saveAiProfileToPreferences = useCallback(
    (formResults: { metier?: string; targetAudience?: string }) => {
      if (!user?.id || !formResults.metier) return;
      try {
        const prefs = user?.preferences;
        const base: IUserPreferences =
          typeof prefs === "string"
            ? (JSON.parse(prefs) as IUserPreferences | null) ?? {}
            : typeof prefs === "object" && prefs !== null
            ? { ...prefs }
            : {};
        base.aiProfile = {
          metier: formResults.metier,
          targetAudience: formResults.targetAudience,
        };
        void dispatch(
          updateUser({
            id: Number(user.id),
            preferences: base,
          }),
        );
      } catch (e) {
        console.error("Failed to save AI profile:", e);
      }
    },
    [user, dispatch],
  );

  const openProfileEditor = useCallback(() => {
    const saved = getSavedAiProfile();
    setEditMetier(saved?.metier ?? "");
    setEditTargetAudience(saved?.targetAudience ?? "");
    const ctx = orgAiContextOverride ?? organization?.ai_context;
    setEditSector(ctx?.sector ?? "");
    setEditDescription(ctx?.description ?? "");
    setIsEditingProfile(true);
  }, [getSavedAiProfile, orgAiContextOverride, organization]);

  const cancelProfileEditor = useCallback(() => {
    setIsEditingProfile(false);
  }, []);

  const toggleProfileEditor = useCallback(() => {
    if (isEditingProfile) {
      setIsEditingProfile(false);
    } else {
      openProfileEditor();
    }
  }, [isEditingProfile, openProfileEditor]);

  const saveProfileEditor = useCallback(() => {
    const metier = editMetier.trim();
    if (!metier) return;
    const formResults = {
      metier,
      targetAudience: editTargetAudience.trim() || undefined,
    };

    const saved = getSavedAiProfile();
    const profilePersistedChange =
      (saved?.metier ?? "") !== metier ||
      (saved?.targetAudience ?? "") !== (formResults.targetAudience ?? "");

    setChatState((prev) => ({ ...prev, userFormResults: formResults }));
    saveAiProfileToPreferences(formResults);

    const sector = editSector.trim() || undefined;
    const description = editDescription.trim() || undefined;
    setOrgAiContextOverride({ sector, description });

    setIsEditingProfile(false);
    if (profilePersistedChange) {
      dispatch(setAlert({ type: "success", msg: t("alerts.profileEditSuccess") }));
    }
  }, [
    editMetier,
    editTargetAudience,
    editSector,
    editDescription,
    getSavedAiProfile,
    saveAiProfileToPreferences,
    dispatch,
    t,
  ]);

  const processRefineResult = useCallback(
    (
      result:
        | ReturnType<typeof refinePlanThunk.fulfilled>
        | ReturnType<typeof refinePlanThunk.rejected>,
      errorFallbackMsg: string,
    ): boolean => {
      if (refinePlanThunk.rejected.match(result)) {
        dispatch(
          setAlert({
            type: "error",
            msg: typeof result.payload === "string" ? result.payload : errorFallbackMsg,
            customTimeoutInMs: 5000,
          }),
        );
        return false;
      }
      const refinedPlan = result.payload.plan;
      const failedImages = (refinedPlan.attachedImages || []).filter((img) => img.failed);
      if (failedImages.length > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.imageAnalysisFailedUpdated"),
            customTimeoutInMs: 5000,
          }),
        );
      }
      const mediaTypeOverrides = result.payload.mediaTypeOverrides;
      const switchesToVideo = mediaTypeOverrides.filter((o) => o.mediaType === "video360");
      if (switchesToVideo.length > 0) {
        const titles = switchesToVideo.map((o) => `"${o.sceneTitle}"`).join(", ");
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.video360Warning", {
              count: switchesToVideo.length,
              titles,
            }),
            customTimeoutInMs: 8000,
          }),
        );
      }
      return true;
    },
    [dispatch, t],
  );

  const handleUserPromptSubmitWithText = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      const prompt = text;
      setUserInput("");
      setIsSubmitting(true);
      setMessages((prev) => [
        ...prev,
        { text: prompt, sender: "user" },
        { text: "", sender: "bot" },
      ]);
      setIsBotTyping(true);

      if (conversation.voiceModeEnabled) {
        ttsSessionOpenRef.current = true;
        conversation.beginSpeakingSession().catch((err) => {
          console.error("Voice mode session error:", err);
          ttsSessionOpenRef.current = false;
        });
      }

      try {
        if (mode === "scenarioHelper") {
          const conversationId = activeConversationRef.current?.id ?? sessionConversationId;
          const currentPhase = phaseRef.current;
          const sourceMediaIds = [
            ...contextPdfs.map((m) => Number(m.id)),
            ...contextImages.map((m) => Number(m.id)),
          ];

          if (!conversationId) {
            sessionStartedNewConvRef.current = true;
            const result = await dispatch(
              startPlanModeChat({
                userPrompt: prompt,
                userFormResults: chatState.userFormResults,
                sourceMediasWithContext: sourceMediaIds,
                chosenLlmProvider,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
                sourceMedias: [...contextPdfs, ...contextImages],
                cb: updateBotMessage,
                cbOnRequestEnd: () => {
                  void dispatch(
                    fetchGptConversations({
                      componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
                      start: 0,
                    }),
                  );
                  void dispatch(syncUserWithServer({}));
                },
              }),
            );
            const created = result.payload as IGptConversation | undefined;
            if (created?.id) {
              setSessionConversationId(created.id);
            }
          } else if (currentPhase === "intake") {
            await dispatch(
              continuePlanModeChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
                sourceMediasWithContext: sourceMediaIds,
                cb: updateBotMessage,
              }),
            );
            void dispatch(syncUserWithServer({}));
          } else if (currentPhase === "ready_to_generate") {
            const result = await dispatch(
              refinePlanThunk({
                conversationId,
                message: prompt,
                ragAgentIds,
                excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
                orgAiContextOverride: orgAiContextOverride ?? undefined,
              }),
            );
            if (!processRefineResult(result, t("pages.chat.refinementFailed"))) {
              updateBotMessage(t("pages.chat.refinementFailed"));
              return;
            }
            setMessages((prev) => {
              const msgs = [...prev];
              const lastIdx = msgs.length - 1;
              if (lastIdx >= 0 && msgs[lastIdx].sender === "bot") {
                msgs[lastIdx] = { text: "__PLAN__", sender: "bot" };
              }
              return msgs;
            });
          } else if (currentPhase === null) {
            await dispatch(
              updateChat({
                userPrompt: prompt,
                conversationId,
                ragAgentIds,
                cb: updateBotMessage,
              }),
            );
            void dispatch(syncUserWithServer({}));
          }
        }
      } finally {
        if (ttsSessionOpenRef.current) {
          conversation.endSpeakingSession();
          ttsSessionOpenRef.current = false;
        }
        setIsSubmitting(false);
        setIsBotTyping(false);
      }
    },
    [
      activeConversation,
      sessionConversationId,
      chatState.userFormResults,
      dispatch,
      mode,
      chosenLlmProvider,
      ragAgentIds,
      contextPdfs,
      contextImages,
      updateBotMessage,
      conversation,
      orgAiContextOverride,
      processRefineResult,
      t,
    ],
  );

  const handleUserPromptSubmit = useCallback(() => {
    if (dictation.isListening) {
      dictation.resetTranscript();
      dictation.stopListening().catch(() => undefined);
    }
    if (conversation.voiceModeEnabled) {
      conversation.disarmAutoListen();
      conversation.stopSpeaking();
    }
    return handleUserPromptSubmitWithText(userInput);
  }, [userInput, handleUserPromptSubmitWithText, conversation, dictation]);

  submitWithTextRef.current = handleUserPromptSubmitWithText;

  const fireReferenceImageChange = useCallback(
    async (change: ScenariaReferenceImageChange, syncedImages: IMedia[]) => {
      if (isSubmitting) return;
      const conversationId = activeConversationRef.current?.id ?? sessionConversationId;
      if (!conversationId) return;
      setIsSubmitting(true);
      setIsBotTyping(true);
      setRealigningDecorTo(change.addMediaId ? "image" : "ai");
      try {
        const result = await dispatch(
          refinePlanThunk({
            conversationId,
            message: "",
            ragAgentIds,
            excludeContextLayers: user?.id ? getExcludedScenariaLayers(user.id) : [],
            orgAiContextOverride: orgAiContextOverride ?? undefined,
            referenceImageChange: change,
          }),
        );
        if (!processRefineResult(result, t("pages.chat.referenceImageChangeFailed"))) return;
        if (activeConversationRef.current?.id === conversationId) {
          dispatch(setScenarioHelperContextImages(syncedImages));
        }
        setPendingReplaceReferenceImage(null);
      } finally {
        setIsSubmitting(false);
        setIsBotTyping(false);
        setRealigningDecorTo(null);
      }
    },
    [
      isSubmitting,
      sessionConversationId,
      dispatch,
      ragAgentIds,
      user?.id,
      orgAiContextOverride,
      processRefineResult,
      t,
    ],
  );

  const { isRetrying: isRetryingAnalysis, retry: retryAnalysis } = useRetryImageAnalysis(
    activeConversation?.id,
  );

  const handlePickReferenceImage = useCallback(() => {
    if (isSubmitting) return;
    setIsRefPicking(true);
    openReferenceImageGallery(dispatch);
  }, [isSubmitting, dispatch]);

  const handlePickedReferenceImage = useCallback(
    (media: IMedia) => {
      if (isSubmitting) return;
      if (contextImages.length > 0) {
        setPendingReplaceReferenceImage(media);
        dispatch(openStandardModal(replaceReferenceImageConfirmId));
      } else {
        void fireReferenceImageChange({ addMediaId: Number(media.id) }, [media]);
      }
    },
    [isSubmitting, contextImages, dispatch, fireReferenceImageChange],
  );

  const handleRemoveReferenceImage = useCallback(
    (media: IMedia) => {
      if (isSubmitting) return;
      setPendingRemoveReferenceImage(media);
      dispatch(openStandardModal(removeReferenceImageConfirmId));
    },
    [isSubmitting, dispatch],
  );

  const handleEnterConfigure = useCallback(() => {
    setUiStage("configure");
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (userInput.trim() && !isSubmitting) void handleUserPromptSubmit();
      }
    },
    [userInput, isSubmitting, handleUserPromptSubmit],
  );

  const visibleMessages = useMemo(() => {
    if (isPlanMode && phase !== "intake" && intakeHistoryBoundary !== null && !showIntakeHistory) {
      return messages.slice(intakeHistoryBoundary);
    }
    return messages;
  }, [messages, isPlanMode, phase, intakeHistoryBoundary, showIntakeHistory]);

  const lastPlanIdx = useMemo(() => {
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      if (visibleMessages[i].sender === "bot" && visibleMessages[i].text === "__PLAN__") {
        return i;
      }
    }
    return -1;
  }, [visibleMessages]);

  const renderedMessages = useMemo(
    () =>
      visibleMessages.map((message, idx) => {
        if (message.sender === "bot" && message.text === "__PLAN__") {
          const isLatestPlan = idx === lastPlanIdx;
          return (
            <div key={idx}>
              {isLatestPlan && plan ? (
                <div className="shrink-0 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <DecorStatusRow
                    plan={plan}
                    contextImages={contextImages}
                    busy={isSubmitting}
                    onPick={phase === "ready_to_generate" ? handlePickReferenceImage : undefined}
                    onRemove={
                      phase === "ready_to_generate" ? handleRemoveReferenceImage : undefined
                    }
                    onRetryAnalysis={phase === "ready_to_generate" ? retryAnalysis : undefined}
                    isRetryingAnalysis={isRetryingAnalysis}
                    realigningTo={realigningDecorTo}
                    className="px-3 py-2 bg-purple-50/40 border-b border-gray-100"
                  />
                  <PlanPreview plan={plan} />
                  <div className="px-3 pb-3 pt-1">
                    <GraphBubble
                      markdown={renderPlanToMarkdown(plan)}
                      mdStringId={`plan-${activeConversation?.id ?? "x"}-${idx}`}
                      flat
                    />
                  </div>
                  {phase === "ready_to_generate" && (
                    <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                        <IoMdInformationCircleOutline className="shrink-0" />
                        <span>{t("pages.chat.readyToGenerateHint")}</span>
                      </div>
                      <button
                        className="btn-primary-fill text-sm"
                        disabled={isSubmitting}
                        onClick={handleEnterConfigure}
                      >
                        {t("pages.chat.generateModule")}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex justify-start">
                  <div className="px-3 py-1.5 rounded-md bg-gray-100 border border-gray-200 text-xs text-gray-400 italic">
                    {t("pages.chat.previousPlanReplaced")}
                  </div>
                </div>
              )}
            </div>
          );
        }

        if (message.sender === "user" && !message.text.trim()) {
          return null;
        }

        return (
          <div key={idx}>
            <ChatBubble customKey={idx} sender={message.sender} text={message.text} />
            {!isPlanMode && isMarkdownShapedLikeAScenario(message.text) && (
              <>
                <GraphBubble
                  markdown={message.text}
                  mdStringId={generateHashFromString(message.text)}
                />
                {!isSubmitting && message.text && (
                  <div className="flex w-full justify-center">
                    <GeneratePdfFromScenarioHelperResponse
                      markdown={message.text}
                      key={`${message.text}-${idx}`}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        );
      }),
    [
      visibleMessages,
      lastPlanIdx,
      plan,
      phase,
      isSubmitting,
      dispatch,
      t,
      isPlanMode,
      contextImages,
      handlePickReferenceImage,
      handleRemoveReferenceImage,
      handleEnterConfigure,
      retryAnalysis,
      isRetryingAnalysis,
      realigningDecorTo,
      activeConversation?.id,
    ],
  );

  const typedUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user" && m.text.trim().length > 0).length,
    [messages],
  );
  const totalUserTurns = useMemo(
    () => messages.filter((m) => m.sender === "user").length,
    [messages],
  );
  const attachmentCount = contextPdfs.length + contextImages.length;
  const skipState = useMemo(
    () => canSkipIntake({ typedUserTurns, totalUserTurns, attachmentCount }),
    [typedUserTurns, totalUserTurns, attachmentCount],
  );

  const handleSkipIntake = useCallback(async () => {
    if (!activeConversation?.id) return;
    setIsBotTyping(true);
    setIsSubmitting(true);
    try {
      const result = await dispatch(
        transitionToReady({ conversationId: activeConversation.id, ragAgentIds }),
      );
      if (transitionToReady.rejected.match(result)) {
        dispatch(
          setAlert({
            type: "error",
            msg:
              typeof result.payload === "string"
                ? result.payload
                : t("pages.chat.planConsolidationFailed"),
            customTimeoutInMs: 5000,
          }),
        );
        return;
      }
      const planResult = result.payload.plan;
      const failedImages = (planResult.attachedImages || []).filter((img) => img.failed);
      if (failedImages.length > 0) {
        dispatch(
          setAlert({
            type: "warning",
            msg: t("pages.chat.imageAnalysisFailedGenerated"),
            customTimeoutInMs: 5000,
          }),
        );
      }
      setMessages((prev) => [
        ...prev,
        { text: "", sender: "user" },
        { text: "__PLAN__", sender: "bot" },
      ]);
    } finally {
      setIsBotTyping(false);
      setIsSubmitting(false);
    }
  }, [activeConversation?.id, dispatch, ragAgentIds, t]);

  const isConfigureStage =
    mode === "scenarioHelper" && phase === "ready_to_generate" && uiStage === "configure";

  return (
    <div className="flex h-full">
      <div className="flex m-2 w-full flex-col items-center">
        <div className="w-full h-full p-4 bg-white shadow-lg rounded-lg flex flex-col relative">
          {mode === "scenarioHelper" && (
            <div className="flex items-center gap-3 flex-wrap mb-2 px-1 text-xs text-gray-500">
              <ContextSelector
                className="relative"
                i18nBase="pages.chat.contextSelector"
                mandatoryLayers={SCENARIA_MANDATORY_LAYERS}
                optionalLayers={SCENARIA_OPTIONAL_LAYERS}
                sourceKey={user?.id}
                disabled={!user?.id}
                prefs={{
                  getExcluded: () => (user?.id ? getExcludedScenariaLayers(user.id) : []),
                  setExcluded: (e) => {
                    if (user?.id) {
                      setExcludedScenariaLayers(user.id, e as ScenariaOptionalLayer[]);
                    }
                  },
                }}
              />
              <span className="flex-1 min-w-0 truncate">
                {aiContextParts.length > 0 && (
                  <>
                    {t("pages.chat.aiContext")} {aiContextParts.join(" · ")}
                    {(!orgCtx || !hasUserProfile) && <span className="ml-2 text-gray-400">—</span>}
                  </>
                )}
                {!hasUserProfile && !orgCtx && (
                  <span className="text-amber-500">
                    {t("pages.chat.aiContextHintBoth")}{" "}
                    <button
                      type="button"
                      onClick={openProfileEditor}
                      className="underline hover:text-amber-600"
                    >
                      {t("pages.chat.aiContextHintProfile")}
                    </button>{" "}
                    {t("pages.chat.aiContextHintAnd")} {t("pages.chat.aiContextHintOrg")}{" "}
                    {t("pages.chat.aiContextHintSuffix")}
                  </span>
                )}
                {hasUserProfile && !orgCtx && (
                  <span className="text-amber-500">{t("pages.chat.aiContextOrgHint")}</span>
                )}
                {!hasUserProfile && orgCtx && (
                  <button
                    type="button"
                    onClick={openProfileEditor}
                    className="text-amber-500 hover:text-amber-600"
                  >
                    {t("pages.chat.aiContextProfileHint")}
                  </button>
                )}
              </span>
              {(hasUserProfile || orgCtx) && (
                <button
                  type="button"
                  onClick={toggleProfileEditor}
                  className="shrink-0 text-purple-500 hover:text-purple-600 font-medium"
                >
                  {t("general.edit")}
                </button>
              )}
            </div>
          )}
          {mode === "scenarioHelper" && isEditingProfile && (
            <div className="mb-2 px-1">
              <div className="rounded-md border border-gray-200 bg-white shadow-sm p-3 text-xs">
                <div className="font-medium text-gray-700 mb-2">
                  {t("pages.profile.main.aiProfileTitle")}
                </div>
                <label className="block mb-2">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.main.aiMetier")}
                  </span>
                  <input
                    type="text"
                    value={editMetier}
                    onChange={(e) => setEditMetier(e.target.value)}
                    placeholder={t("pages.profile.main.aiMetierPlaceholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                    autoFocus
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.main.aiTargetAudience")}
                  </span>
                  <input
                    type="text"
                    value={editTargetAudience}
                    onChange={(e) => setEditTargetAudience(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editMetier.trim()) {
                        e.preventDefault();
                        saveProfileEditor();
                      }
                    }}
                    placeholder={t("pages.profile.main.aiTargetAudiencePlaceholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>

                <div className="flex items-center gap-1.5 mb-2 pt-2 border-t border-gray-100">
                  <span className="font-medium text-gray-700">
                    {t("pages.profile.organization.aiContextTitle")}
                  </span>
                  <MuiTooltip title={t("pages.chat.aiOrgFieldsSessionHint")} arrow placement="top">
                    <span className="text-gray-400 cursor-help inline-flex">
                      <IoMdInformationCircleOutline className="w-3.5 h-3.5" />
                    </span>
                  </MuiTooltip>
                </div>
                <label className="block mb-2">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.organization.aiSector")}
                  </span>
                  <input
                    type="text"
                    value={editSector}
                    onChange={(e) => setEditSector(e.target.value)}
                    placeholder={t("pages.profile.organization.aiSectorPlaceholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>
                <label className="block mb-3">
                  <span className="block text-gray-600 mb-1">
                    {t("pages.profile.organization.aiDescription")}
                  </span>
                  <input
                    type="text"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder={t("pages.profile.organization.aiDescriptionPlaceholder")}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                  />
                </label>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn-alternative-outline text-sm"
                    onClick={cancelProfileEditor}
                  >
                    {t("general.cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn-primary-fill text-sm"
                    onClick={saveProfileEditor}
                    disabled={!editMetier.trim()}
                  >
                    {t("general.save")}
                  </button>
                </div>
              </div>
            </div>
          )}
          {mode === "scenarioHelper" && isPlanMode && (
            <PlanModeProgress
              step={
                phase === "intake"
                  ? 1
                  : phase === "ready_to_generate"
                  ? uiStage === "configure"
                    ? 3
                    : 2
                  : phase === "generating"
                  ? 4
                  : phase === "generated"
                  ? 5
                  : 2
              }
            />
          )}
          {isLegacyConversation && !legacyBannerDismissed && (
            <div className="flex items-start gap-3 px-3 py-2 mb-2 rounded-md bg-amber-50 border border-amber-300 text-amber-900">
              <div className="flex-1 text-xs leading-relaxed">
                <div className="font-semibold mb-0.5">{t("pages.chat.legacyBannerTitle")}</div>
                <div>
                  {t("pages.chat.legacyBannerBody")}{" "}
                  <button
                    type="button"
                    className="text-purple-500 hover:text-purple-600 font-medium underline"
                    onClick={resetChatLocally}
                  >
                    {t("pages.chat.legacyBannerLink")}
                  </button>
                  .
                </div>
              </div>
              <button
                type="button"
                aria-label={t("pages.chat.legacyBannerDismiss")}
                className="text-amber-700 hover:text-amber-900 text-lg leading-none px-1"
                onClick={() => setLegacyBannerDismissed(true)}
              >
                ×
              </button>
            </div>
          )}
          {isConfigureStage && activeConversation?.id ? (
            <ConfigureGenerationPanel
              plan={plan}
              conversationId={activeConversation.id}
              ragAgentIds={ragAgentIds}
              lockedDestinationProjectGroupId={lockedDestinationProjectGroupId}
              onBack={() => setUiStage("thread")}
              onGenerationStarted={() => setUiStage("thread")}
              isReferenceImageBusy={isSubmitting}
              referenceImageRealigningTo={realigningDecorTo}
              onPickReferenceImage={handlePickReferenceImage}
              onRemoveReferenceImage={handleRemoveReferenceImage}
            />
          ) : (
            <>
              <div className="chat-window bg-gray-100 p-4 flex-grow overflow-y-scroll rounded-md flex flex-col space-y-4">
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase !== "intake" &&
                  intakeHistoryBoundary !== null && (
                    <button
                      onClick={() => setShowIntakeHistory((v) => !v)}
                      className="text-xs text-gray-500 hover:text-gray-700 underline self-start"
                    >
                      {showIntakeHistory
                        ? t("pages.chat.hideIntakeHistory")
                        : t("pages.chat.showIntakeHistory")}
                    </button>
                  )}
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase !== "intake" &&
                  contextPdfs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-md bg-gray-200/60 text-[11px] text-gray-500">
                      <MdOutlineAttachment size={13} className="shrink-0" />
                      {contextPdfs.map((pdf) => (
                        <span key={pdf.id} className="truncate max-w-[140px]">
                          {pdf.filename || pdf.name}
                        </span>
                      ))}
                    </div>
                  )}
                {mode === "scenarioHelper" &&
                  isPlanMode &&
                  phase === "ready_to_generate" &&
                  plan &&
                  lastPlanIdx === -1 && (
                    <div className="shrink-0 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                      <DecorStatusRow
                        plan={plan}
                        contextImages={contextImages}
                        busy={isSubmitting}
                        onPick={handlePickReferenceImage}
                        onRemove={handleRemoveReferenceImage}
                        onRetryAnalysis={retryAnalysis}
                        isRetryingAnalysis={isRetryingAnalysis}
                        realigningTo={realigningDecorTo}
                        className="px-3 py-2 bg-purple-50/40 border-b border-gray-100"
                      />
                      <PlanPreview plan={plan} />
                      <div className="px-3 pb-3 pt-1">
                        <GraphBubble
                          markdown={renderPlanToMarkdown(plan)}
                          mdStringId={`plan-fallback-${activeConversation?.id ?? "x"}`}
                          flat
                        />
                      </div>
                      <div className="px-3 pb-3 pt-2 flex flex-col items-center gap-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 italic">
                          <IoMdInformationCircleOutline className="shrink-0" />
                          <span>{t("pages.chat.readyToGenerateHint")}</span>
                        </div>
                        <button
                          className="btn-primary-fill text-sm"
                          disabled={isSubmitting}
                          onClick={handleEnterConfigure}
                        >
                          {t("pages.chat.generateModule")}
                        </button>
                      </div>
                    </div>
                  )}
                {renderedMessages}
                {isBotTyping && (
                  <div className="flex justify-start">
                    <div className="px-4 py-3 rounded-xl rounded-bl-sm bg-gray-50 border border-gray-200 text-gray-500">
                      <TypingIndicator />
                    </div>
                  </div>
                )}
                {mode === "scenarioHelper" && phase === "intake" && activeConversation?.id && (
                  <SkipIntakeButton
                    canSkip={skipState.canSkip}
                    reason={skipState.reason}
                    isSubmitting={isSubmitting}
                    onClick={handleSkipIntake}
                  />
                )}
                {mode === "scenarioHelper" &&
                  (phase === "generating" || phase === "generated") &&
                  plan && (
                    <GenerationRecap
                      plan={plan}
                      phase={phase}
                      progressMessage={ownProgressMessage}
                      sourceMedia={[...contextImages, ...contextPdfs]}
                      lastGeneratedProjectGroupId={activeConversation?.lastGeneratedProjectGroupId}
                      isOpeningModule={isOpeningModule}
                      onOpenModule={handleOpenGeneratedModule}
                      onNewScenario={resetChatLocally}
                      onRestartFromPlan={handleRestartFromPlan}
                      isRestarting={isRestarting}
                      generationStartedAt={isProgressOwnConversation ? generationStartedAt : null}
                    />
                  )}
                {mode === "scenarioHelper" &&
                  !activeConversation &&
                  messages.length <= 1 &&
                  !isBotTyping && (
                    <div className="flex-1 flex items-center justify-center">
                      <p className="text-sm text-gray-400 text-center max-w-md">
                        {t("pages.chat.scenarioHelperEmptyHint")}
                      </p>
                    </div>
                  )}
                <div ref={bottomRef} />
              </div>
              {inputMode && (
                <div className="flex-shrink-0 border-t border-gray-200 px-4 pt-3 pb-2 bg-white">
                  <ConversationStatusStrip
                    isSpeaking={conversation.isSpeaking}
                    onStopSpeaking={conversation.stopSpeaking}
                    isThinking={isBotTyping || isSubmitting}
                    isListening={dictation.isListening}
                    hasSpeechStarted={dictation.lastActivityAt !== null}
                    planMessage={
                      mode === "scenarioHelper"
                        ? isRealigningDecor
                          ? t("pages.chat.decorRealignInProgress")
                          : ownProgressMessage
                        : null
                    }
                    scenarioPhase={mode === "scenarioHelper" ? phase ?? null : null}
                    hasMediaProcessing={hasMediaProcessing}
                  />
                  <div className="flex items-start justify-between gap-3 mb-1.5 min-h-[28px]">
                    <div className="flex-1 min-w-0">
                      {mode === "scenarioHelper" && (
                        <AttachContextMedia
                          isEditionEnabled={(!phase || phase === "intake") && !isSubmitting}
                          isImagePickBusy={isSubmitting}
                          hideDecorImages={realigningDecorTo === "ai"}
                          onImageButtonOverride={
                            phase === "ready_to_generate" ? handlePickReferenceImage : undefined
                          }
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <VoiceModeToggle
                        enabled={conversation.voiceModeEnabled}
                        onToggle={conversation.toggleVoiceMode}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-end">
                    <textarea
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      readOnly={dictation.isListening}
                      className={`flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-400 transition-all overflow-y-auto max-h-40 ${
                        dictation.isListening
                          ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                      }`}
                      placeholder={
                        phase === "generated"
                          ? t("pages.chat.inputPlaceholderGenerated")
                          : phase === "generating"
                          ? t("pages.chat.inputPlaceholderGenerating")
                          : phase === "ready_to_generate"
                          ? t("pages.chat.inputPlaceholderRefine")
                          : t("pages.chat.scenarioHelperInputPlaceholder")
                      }
                      disabled={isSubmitting || phase === "generating" || phase === "generated"}
                    />
                    <MicrophoneButton
                      isListening={dictation.isListening}
                      isSupported={dictation.isSupported}
                      disabled={isSubmitting}
                      idleTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationStartTooltip"
                          : "voice.dictateTooltip"
                      }
                      listeningTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationListeningTooltip"
                          : "voice.dictateListeningTooltip"
                      }
                      listeningWaitingTooltipKey={
                        conversation.voiceModeEnabled
                          ? "voice.conversationListeningWaitingTooltip"
                          : "voice.dictateListeningWaitingTooltip"
                      }
                      cooldown={
                        conversation.voiceModeEnabled
                          ? { triggerAt: dictation.lastActivityAt, durationMs: 1500 }
                          : undefined
                      }
                      onStartListening={() => {
                        if (conversation.voiceModeEnabled && conversation.isSpeaking) {
                          conversation.stopSpeaking();
                        }
                        dictation.resetTranscript();
                        dictation
                          .startListening()
                          .catch((err) => console.error("STT start error:", err));
                      }}
                      onStopListening={() => {
                        conversation.disarmAutoListen();
                        dictation.stopListening().catch(() => undefined);
                      }}
                    />
                    <Tooltip
                      title={submitDisabledReason}
                      disableHoverListener={!submitDisabledReason}
                    >
                      <span>
                        <button
                          onClick={handleUserPromptSubmit}
                          disabled={isSubmitDisabled}
                          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600 rounded-xl transition-colors"
                        >
                          <IoMdSend size={16} />
                        </button>
                      </span>
                    </Tooltip>
                  </div>
                  <div className="flex justify-between items-center mt-1.5">
                    {mode === "scenarioHelper" && (
                      <LlmProviderDropdown
                        defaultValue={chosenLlmProvider}
                        onChange={(value) => dispatch(setChosenLlmProvider(value))}
                        disabled={Boolean(activeConversation) || isSubmitting}
                      />
                    )}
                    <p className="text-xs text-gray-400 ml-auto">
                      {t("pages.chat.shiftEnterHint")}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <IsLoading componentId={CHATBOT_COMPONENT_ID} showSuccess={false} />

      <ModalStandard id={replaceReferenceImageConfirmId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">
            {t("pages.chat.replaceReferenceImageConfirmBody", {
              oldName: contextImages[0]?.filename || contextImages[0]?.name || "",
              newName:
                pendingReplaceReferenceImage?.filename || pendingReplaceReferenceImage?.name || "",
            })}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => {
                dispatch(closeStandardModal(replaceReferenceImageConfirmId));
                setPendingReplaceReferenceImage(null);
              }}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isSubmitting}
              onClick={() => {
                dispatch(closeStandardModal(replaceReferenceImageConfirmId));
                if (!pendingReplaceReferenceImage) return;
                void fireReferenceImageChange(
                  {
                    addMediaId: Number(pendingReplaceReferenceImage.id),
                    removeMediaIds: contextImages.map((i) => Number(i.id)),
                  },
                  [pendingReplaceReferenceImage],
                );
              }}
            >
              {t("general.confirm")}
            </button>
          </div>
        </div>
      </ModalStandard>

      <ModalStandard id={removeReferenceImageConfirmId} modalType="standard" className="w-[28rem]">
        <div className="p-2 space-y-4">
          <p className="text-sm text-gray-700">{t("pages.chat.removeReferenceImageConfirmBody")}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-alternative-outline"
              onClick={() => {
                dispatch(closeStandardModal(removeReferenceImageConfirmId));
                setPendingRemoveReferenceImage(null);
              }}
            >
              {t("general.cancel")}
            </button>
            <button
              type="button"
              className="btn-primary-fill"
              disabled={isSubmitting}
              onClick={() => {
                dispatch(closeStandardModal(removeReferenceImageConfirmId));
                if (!pendingRemoveReferenceImage) return;
                const removedId = Number(pendingRemoveReferenceImage.id);
                setPendingRemoveReferenceImage(null);
                void fireReferenceImageChange(
                  { removeMediaIds: [removedId] },
                  contextImages.filter((i) => Number(i.id) !== removedId),
                );
              }}
            >
              {t("general.confirm")}
            </button>
          </div>
        </div>
      </ModalStandard>

      <ReferenceImageGalleryHost
        open={isRefPicking}
        onClosed={() => setIsRefPicking(false)}
        onPicked={handlePickedReferenceImage}
      />
    </div>
  );
};

export default React.memo(Chatbot);
